import React, { useEffect, useState } from 'react';
import renderModal from '@components/show_page/modals/renderModal';
import InputContainer from '@components/library/input_container/InputContainer';
import {
  CloseButton,
  PrimaryButton,
  TextWarningButton,
} from '@components/library/button/index';
import { InputType } from '@components/library/inputs/Input.props';
import FilestackUploader from '@components/common/filestack_uploader/main';
import { FileStackOnUpload } from '@components/common/filestack_uploader/FileStackTypes';

import { getPortalCard, createPortalCard, updatePortalCard } from '@api/v4/private/portal_cards';
import { PortalCardTypes, PortalCardSubTypes, ValidationErrors } from '@api/v4/private/PortalCardTypes';
import { makeValidUrl } from '../helpers/make_valid_url';
import { handleDeletion } from '../helpers/card_deletion_helper';
import { handleValidations, nameError, linkError } from '../portal_cards/validations';

interface LinkFormProps {
  closeModal: () => void;
  defaultSubType?: PortalCardSubTypes;
  portalCardId?: string;
  triggerCardsRefresh: () => void;
}

const LinkForm: React.FunctionComponent<LinkFormProps> = ({
  closeModal,
  defaultSubType,
  portalCardId,
  triggerCardsRefresh
}): JSX.Element => {
  const type = PortalCardTypes.PortalLink;
  const [iconUrl, setIconUrl] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [name, setName] = useState("");
  const [subType, setSubType] = useState(defaultSubType || PortalCardSubTypes.InternalLink);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors[]>([]);
  const portalId = (BFG.resource.type === 'portal') ? BFG.resource.key : null;
  const invalidSubmission = (validationErrors.length > 0);

  useEffect(() => {
    if (portalCardId) {
      getPortalCard({ portalCardId })
        .then((response) => {
          const { attributes } = response.data;
          setIconUrl(attributes.icon_url);
          setLinkUrl(attributes.link_url);
          setName(attributes.name);
          setSubType(PortalCardSubTypes[attributes.sub_type]);
        });
    }
  }, [portalCardId]);

  const requestBody = {
    iconUrl,
    linkUrl,
    name,
    subType,
    type
  };

  const submitCreateCard = ((): void => {
    if (handleValidations(type, setValidationErrors, requestBody)) {
      return;
    }

    createPortalCard(portalId, requestBody)
      .then(() => {
        triggerCardsRefresh();
        closeModal();
      })
      .catch(() => {
        Notify.create({
          title: t("Something went wrong!"),
          body: t("Please try creating this link again."),
          type: "error"
        });
        closeModal();
      });
  });

  const submitUpdateCard = ((): void => {
    if (handleValidations(type, setValidationErrors, requestBody)) {
      return;
    }

    updatePortalCard(portalCardId, requestBody)
      .then(() => {
        triggerCardsRefresh();
        closeModal();
      })
      .catch(() => {
        Notify.create({
          title: t("Something went wrong!"),
          body: t("Please try updating this link again."),
          type: "error"
        });
        closeModal();
      });
  });

  // TODO: Change all the references to tile-
  // Don't want to totally break styles, so holding on changing the names
  return (
    <div className="modal-content-wrapper portal-modal tile-form">
      <div className="portal-modal__header">
        {portalCardId ? (
          <span className="bff-edit" aria-hidden="true" />
        ) : (
          <span className="bff-plus" aria-hidden="true" />
        )}
        <h3 className="modal-title">
          {portalCardId
            ? `${t("Edit")} ${name}`
            : `${t("Create")} ${(subType === PortalCardSubTypes.ExternalLink)
              ? t("External Link")
              : t("Navigation Link")}`
          }
        </h3>
        <CloseButton onClick={closeModal} />
      </div>
      <div className="portal-modal__body">
        <div className="tile-form__name">
          <div className="tile-form__character-limit">
            <InputContainer
              attributes={{
                name: "link-name",
                onChange: (e: InputChangeEvent): void => setName(e.target.value),
                type: InputType.Text,
              }}
              input={{ value: name, error: nameError(validationErrors) }}
              labelCopy={`${t("Name")} *`}
              submitAttempted={invalidSubmission}
            />
          </div>
        </div>
        <InputContainer
          attributes={{
            name: "link-url",
            onChange: (e: InputChangeEvent): void => setLinkUrl(e.target.value),
            onBlur: (): void => setLinkUrl(makeValidUrl(linkUrl)),
            type: InputType.Text,
          }}
          input={{ value: linkUrl, error: linkError(validationErrors) }}
          labelCopy={`${t("Link URL")} *`}
          submitAttempted={invalidSubmission}
        />
        <div className="portal-modal__filestack-container">
          <div className="portal-modal__filestack-container--description">
            <h3>{t("Link Icon")}</h3>
            <p>{t("Recommended size: 30 x 30px")}</p>
          </div>
          {iconUrl ? (
            <div className="portal-modal__filestack-container--thumbnail">
              <div className="portal-modal__filestack-container--thumbnail-container">
                <img className="portal-modal__filestack-container--thumbnail--image" src={iconUrl} alt="link icon" />
              </div>
              <TextWarningButton
                icon="bff-trash"
                onClick={(): void => setIconUrl(null)}
              />
            </div>
          )
            : (
              <FilestackUploader
                options={{
                  maxFiles: 1,
                  accept: "image/*",
                  transformations: { crop: { aspectRatio: (64 / 64), force: true } }
                }}
                type="area"
                onSuccess={(files: FileStackOnUpload): void => setIconUrl(files.filesUploaded[0].url)}
              />
            )
        }
        </div>
        <div className="portal-modal__button">
          {portalCardId
            && (
              <div className="portal-modal__two-buttons">
                <TextWarningButton
                  icon="bff-trash"
                  onClick={(): void => handleDeletion(portalCardId, name, closeModal, triggerCardsRefresh)}
                >
                  {t("Delete Link")}
                </TextWarningButton>
                <PrimaryButton
                  onClick={submitUpdateCard}
                  size="medium"
                >
                  {t("Update Link")}
                </PrimaryButton>
              </div>
            )
          }
          {!portalCardId && (
            <PrimaryButton
              onClick={submitCreateCard}
              size="small"
            >
              {t("Create Link")}
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  );
};

const ModalComponent = renderModal(LinkForm, "LinkForm");
export default ModalComponent;
